import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, ArrowLeft, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { Textarea } from '../components/ui/textarea';
import { useExperienceTheme } from '../context/ExperienceThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Communication = () => {
  const navigate = useNavigate();
  const { currentTheme, experienceTheme } = useExperienceTheme();
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [recentMessages, setRecentMessages] = useState([]);

  // Theme terminology
  const t = currentTheme?.terminology || {};
  const quickMessages = currentTheme?.quickMessages || [
    "I need a moment",
    "Feeling overwhelmed",
    "Can't talk right now",
    "Need help",
    "I'm okay, just quiet",
  ];

  useEffect(() => {
    fetchTodaySession();
  }, []);

  const fetchTodaySession = async () => {
    try {
      const response = await fetch(`${API}/session/today`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setRecentMessages(data.bridge_messages || []);
      }
    } catch (error) {
      console.error('Failed to fetch session:', error);
    }
  };

  const handleSendMessage = async () => {
    if (!message.trim()) return;

    setSending(true);
    try {
      const response = await fetch(`${API}/bridge-message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ message: message.trim() }),
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setMessage('');
        setRecentMessages((prev) => [data.bridge_message, ...prev]);
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error(`Signal transmission failed. Try again, ${t.userRole || 'Pilot'}.`);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen theme-bg">
      {/* Background */}
      <div className="fixed inset-0 theme-gradient opacity-50 pointer-events-none" />

      {/* Header */}
      <header className="relative z-20 px-4 py-4 md:px-8 md:py-6">
        <div className="max-w-2xl mx-auto flex items-center gap-4">
          <button
            data-testid="back-btn"
            onClick={() => navigate('/dashboard')}
            className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center theme-text-muted hover:theme-text hover:bg-white/10 transition-all"
          >
            <ArrowLeft size={18} />
          </button>
          <div>
            <h1 className="theme-text text-lg md:text-xl font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {t.communication || 'Communication'}
            </h1>
            <p className="theme-text-muted text-sm">
              {t.communicationDesc || 'Send a signal when words are hard'}
            </p>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 px-4 md:px-8 pb-12">
        <div className="max-w-2xl mx-auto space-y-6">
          {/* Message Input Card */}
          <div className="card-modern p-6 animate-fade-in-up">
            <Textarea
              data-testid="communication-message-input"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={`Type your message here, ${t.userRole || 'Pilot'}...`}
              className="input-modern w-full min-h-[140px] resize-none mb-4"
            />
            <button
              data-testid="send-communication-btn"
              onClick={handleSendMessage}
              disabled={!message.trim() || sending}
              className="btn-primary w-full py-4 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send size={18} />
              {sending ? 'Transmitting...' : (t.communicationAction || 'Send Signal')}
            </button>
          </div>

          {/* Quick Messages */}
          <div className="animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
            <p className="theme-text-muted text-sm mb-3">Quick signals:</p>
            <div className="flex flex-wrap gap-2">
              {quickMessages.map((msg, idx) => (
                <button
                  key={idx}
                  data-testid={`quick-message-${idx}`}
                  onClick={() => setMessage(msg)}
                  className="tag-modern"
                >
                  {msg}
                </button>
              ))}
            </div>
          </div>

          {/* Recent Messages */}
          {recentMessages.length > 0 && (
            <div className="animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              <p className="theme-text-muted text-sm mb-3 flex items-center gap-2">
                <Clock size={14} />
                Today's signals
              </p>
              <div className="space-y-2">
                {recentMessages.slice(0, 5).map((msg, idx) => (
                  <div
                    key={msg.id || idx}
                    className="card-modern p-4 border-l-2"
                    style={{ borderLeftColor: currentTheme?.colors?.secondary || '#bc13fe' }}
                  >
                    <p className="theme-text-secondary text-sm">{msg.message}</p>
                    <p className="theme-text-muted text-xs mt-2">
                      {new Date(msg.timestamp).toLocaleTimeString()}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Encouragement */}
          <div className="text-center py-6 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
            <p className="theme-text-muted text-sm">
              You showed courage by opening {t.communication || 'Communication'} today.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Communication;
